import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import UploadIcon from '@mui/icons-material/CloudUpload';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CategoryIcon from '@mui/icons-material/Category';
import axiosInstance from '../../services/axiosInstance';
import { ICategory } from '../../types/damTypes';
import { v4 as uuidv4 } from 'uuid';

type Step = 'choose-main' | 'choose-sub' | 'choose-sub2' | 'choose-files' | 'metadata';

type UploadItem = {
  id: string;
  file: File;
  previewUrl: string;
  butikId: string;
  description: string;
};

function normalizeName(input: string): string {
  return (input || '')
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics (ö -> o)
    .replace(/\s+/g, ' ');
}

function getParentId(cat: ICategory): string | null {
  const p: any = (cat as any).parentCategory;
  if (!p) return null;
  if (typeof p === 'string') return p;
  if (typeof p === 'object' && typeof p._id === 'string') return p._id;
  return null;
}

export default function UploadScreen(props: {
  categories: ICategory[];
  loadingCategories: boolean;
  onUploadSuccess: () => void;
}) {
  const { categories, loadingCategories, onUploadSuccess } = props;

  const [step, setStep] = React.useState<Step>('choose-main');
  const [mainCategoryId, setMainCategoryId] = React.useState<string>('');
  const [subCategoryId, setSubCategoryId] = React.useState<string>('');
  const [subSubCategoryId, setSubSubCategoryId] = React.useState<string>('');

  const [uploadItems, setUploadItems] = React.useState<UploadItem[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  // Bulk metadata (apply to all) helpers
  const [bulkButikId, setBulkButikId] = React.useState('');
  const [bulkDescription, setBulkDescription] = React.useState('');
  const bulkButikIdRef = React.useRef('');
  const bulkDescriptionRef = React.useRef('');

  const butikMain = React.useMemo(() => {
    const wanted = normalizeName('butiksbilder');
    return categories.find((c) => normalizeName(c.name) === wanted) || null;
  }, [categories]);

  const ovrigtMain = React.useMemo(() => {
    const wanted = normalizeName('övrigt');
    // After normalization, "övrigt" becomes "ovrigt".
    return categories.find((c) => normalizeName(c.name) === wanted) || null;
  }, [categories]);

  const selectedMain = React.useMemo(() => {
    return categories.find((c) => c._id === mainCategoryId) || null;
  }, [categories, mainCategoryId]);

  const subcategories = React.useMemo(() => {
    if (!mainCategoryId) return [];
    return categories
      .filter((c) => getParentId(c) === mainCategoryId)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'));
  }, [categories, mainCategoryId]);

  const selectedSub = React.useMemo(() => {
    return categories.find((c) => c._id === subCategoryId) || null;
  }, [categories, subCategoryId]);

  const subSubcategories = React.useMemo(() => {
    if (!subCategoryId) return [];
    return categories
      .filter((c) => getParentId(c) === subCategoryId)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'));
  }, [categories, subCategoryId]);

  const isButiksbilderFlow = React.useMemo(() => {
    return !!butikMain && mainCategoryId === butikMain._id;
  }, [butikMain, mainCategoryId]);

  const hasThirdLevel = React.useMemo(() => {
    return subSubcategories.length > 0 || step === 'choose-sub2' || !!subSubCategoryId;
  }, [step, subSubCategoryId, subSubcategories.length]);

  const accentBlue = '#072f53';

  const breadcrumb = React.useMemo(() => {
    const base = [
      { key: 'choose-main' as Step, label: 'Kategori' },
      { key: 'choose-sub' as Step, label: isButiksbilderFlow ? 'Kedja' : 'Underkategori' },
    ];
    const third = hasThirdLevel
      ? [{ key: 'choose-sub2' as Step, label: isButiksbilderFlow ? 'Butikstyp' : 'Underkategori 2' }]
      : [];
    const tail = [
      { key: 'choose-files' as Step, label: 'Bilder' },
      { key: 'metadata' as Step, label: 'Metadata' },
    ];
    return [...base, ...third, ...tail];
  }, [hasThirdLevel, isButiksbilderFlow]);

  const applyBulkButik = (next: string) => {
    const prevBulk = bulkButikIdRef.current;
    bulkButikIdRef.current = next;
    setBulkButikId(next);
    setUploadItems(prev => prev.map(it => {
      const current = it.butikId || '';
      const shouldOverwrite = current.trim().length === 0 || current === prevBulk;
      return shouldOverwrite ? { ...it, butikId: next } : it;
    }));
  };

  const applyBulkDescription = (next: string) => {
    const prevBulk = bulkDescriptionRef.current;
    bulkDescriptionRef.current = next;
    setBulkDescription(next);
    setUploadItems(prev => prev.map(it => {
      const current = it.description || '';
      const shouldOverwrite = current.trim().length === 0 || current === prevBulk;
      return shouldOverwrite ? { ...it, description: next } : it;
    }));
  };

  const resetAll = () => {
    try {
      uploadItems.forEach((it) => {
        if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      });
    } catch {}
    setStep('choose-main');
    setMainCategoryId('');
    setSubCategoryId('');
    setSubSubCategoryId('');
    setUploadItems([]);
    setError(null);
    setSuccess(null);
    setBulkButikId('');
    setBulkDescription('');
    bulkButikIdRef.current = '';
    bulkDescriptionRef.current = '';
    try {
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {}
  };

  // Cleanup blob URLs when items change/unmount
  React.useEffect(() => {
    return () => {
      try {
        uploadItems.forEach((it) => {
          if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
        });
      } catch {}
    };
  }, [uploadItems]);

  const handlePickFiles = () => fileInputRef.current?.click();

  const handleUpload = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!mainCategoryId) {
        setError('Välj kategori.');
        return;
      }
      if (!subCategoryId && subcategories.length > 0) {
        setError('Välj underkategori.');
        return;
      }
      if (!subSubCategoryId && subSubcategories.length > 0) {
        setError('Välj butikstyp.');
        return;
      }
      if (uploadItems.length === 0) {
        setError('Välj minst en bild.');
        return;
      }

      setUploading(true);

      let successCount = 0;
      let failCount = 0;

      for (const item of uploadItems) {
        try {
          const formData = new FormData();
          formData.append('asset', item.file);
          // Idempotency (single-file): prevents duplicates if user retries after timeout.
          formData.append('clientUploadId', item.id);

          // Categories: main + (optional) level 2 + (optional) level 3
          formData.append('categories', mainCategoryId);
          if (subCategoryId) formData.append('categories', subCategoryId);
          if (subSubCategoryId) formData.append('categories', subSubCategoryId);

          // Per-file description
          if (item.description) formData.append('description', item.description);

          // Butiksbilder: only one template field + description
          if (isButiksbilderFlow && item.butikId && item.butikId.trim()) {
            formData.append('metadata.butik_id', item.butikId.trim());
          }

          await axiosInstance.post(`/api/assets`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000,
          });
          successCount++;
        } catch (e) {
          console.error('Upload failed for file:', item.file?.name, e);
          failCount++;
        }
      }

      if (failCount === 0) {
        setSuccess(`Uppladdning klar! (${successCount}/${uploadItems.length})`);
        onUploadSuccess();
        resetAll();
      } else {
        setError(`Uppladdning delvis klar: ${successCount}/${uploadItems.length} lyckades. Försök igen för att ladda upp resterande.`);
        setSuccess(null);
      }
    } catch (err: any) {
      console.error('Mobile upload error:', err);
      setError(err?.response?.data?.message || err?.message || 'Uppladdning misslyckades');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: '#f5f5f5' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Ladda upp
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
          {breadcrumb.map((b, idx) => {
            const isActive = b.key === step;
            return (
              <React.Fragment key={b.key}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    fontWeight: isActive ? 800 : 500,
                    color: isActive ? accentBlue : 'text.secondary',
                  }}
                >
                  {b.label}
                </Typography>
                {idx < breadcrumb.length - 1 && (
                  <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                    →
                  </Typography>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {loadingCategories ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (!butikMain || !ovrigtMain) ? (
          <Alert severity="warning">
            Hittar inte kategorierna <strong>Butiksbilder</strong> och/eller <strong>Övrigt</strong>.
            Kontrollera att de finns exakt som huvudkategorier i “Kategorier”.
          </Alert>
        ) : (
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            {step === 'choose-main' && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  1) Välj kategori
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.25 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setMainCategoryId(butikMain._id);
                      setSubCategoryId('');
                      setSubSubCategoryId('');
                      setUploadItems([]);
                      setError(null);
                      setSuccess(null);
                      setBulkButikId('');
                      setBulkDescription('');
                      bulkButikIdRef.current = '';
                      bulkDescriptionRef.current = '';
                      setStep('choose-sub');
                    }}
                    sx={{
                      py: 2.25,
                      fontSize: '1.05rem',
                      fontWeight: 800,
                      borderRadius: 2,
                      minHeight: 96,
                      backgroundColor: 'white',
                      borderColor: 'divider',
                      textTransform: 'none',
                      '&:hover': { borderColor: accentBlue, backgroundColor: 'rgba(7,47,83,0.04)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <PhotoCameraIcon sx={{ fontSize: 34 }} />
                      <span>Butiksbilder</span>
                    </Box>
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setMainCategoryId(ovrigtMain._id);
                      setSubCategoryId('');
                      setSubSubCategoryId('');
                      setUploadItems([]);
                      setError(null);
                      setSuccess(null);
                      setBulkButikId('');
                      setBulkDescription('');
                      bulkButikIdRef.current = '';
                      bulkDescriptionRef.current = '';
                      setStep('choose-sub');
                    }}
                    sx={{
                      py: 2.25,
                      fontSize: '1.05rem',
                      fontWeight: 800,
                      borderRadius: 2,
                      minHeight: 96,
                      backgroundColor: 'white',
                      borderColor: 'divider',
                      textTransform: 'none',
                      '&:hover': { borderColor: accentBlue, backgroundColor: 'rgba(7,47,83,0.04)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <CategoryIcon sx={{ fontSize: 34 }} />
                      <span>Övrigt</span>
                    </Box>
                  </Button>
                </Box>
              </>
            )}

            {step === 'choose-sub' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                      setStep('choose-main');
                      setMainCategoryId('');
                      setSubCategoryId('');
                      setSubSubCategoryId('');
                      setUploadItems([]);
                    }}
                  >
                    Tillbaka
                  </Button>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    2) Välj {isButiksbilderFlow ? 'kedja' : 'underkategori'}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Kategori: <strong>{selectedMain?.name}</strong>
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                {subcategories.length === 0 ? (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Inga underkategorier hittades för {selectedMain?.name}. Du kan ladda upp direkt till huvudkategorin.
                    </Alert>
                    <Button
                      variant="contained"
                      onClick={() => setStep('choose-files')}
                      sx={{ py: 1.75, fontWeight: 800, borderRadius: 2 }}
                      fullWidth
                    >
                      Fortsätt
                    </Button>
                  </>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1.25 }}>
                    {subcategories.map((sc) => (
                      <Button
                        key={sc._id}
                        variant={subCategoryId === sc._id ? 'contained' : 'outlined'}
                        onClick={() => {
                          setSubCategoryId(sc._id);
                          setSubSubCategoryId('');
                          setUploadItems([]);
                          setError(null);
                          setSuccess(null);
                          const hasChildren = categories.some((c) => getParentId(c) === sc._id);
                          setStep(hasChildren ? 'choose-sub2' : 'choose-files');
                        }}
                        sx={{ py: 2, fontWeight: 800, borderRadius: 2, minHeight: 96 }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <StorefrontIcon />
                          <span style={{ textAlign: 'center', lineHeight: 1.1 }}>{sc.name}</span>
                        </Box>
                      </Button>
                    ))}
                  </Box>
                )}
              </>
            )}

            {step === 'choose-sub2' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                      setStep('choose-sub');
                      setSubSubCategoryId('');
                      setUploadItems([]);
                    }}
                  >
                    Tillbaka
                  </Button>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    3) Välj {isButiksbilderFlow ? 'butikstyp' : 'underkategori'}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedMain?.name ? <>Kategori: <strong>{selectedMain.name}</strong></> : null}
                  {selectedSub?.name ? <> {' '}→{' '}<strong>{selectedSub.name}</strong></> : null}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                {subSubcategories.length === 0 ? (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Inga {isButiksbilderFlow ? 'butikstyper' : 'underkategorier'} hittades under {selectedSub?.name}. Du kan ladda upp direkt.
                    </Alert>
                    <Button
                      variant="contained"
                      onClick={() => setStep('choose-files')}
                      sx={{ py: 1.75, fontWeight: 800, borderRadius: 2 }}
                      fullWidth
                    >
                      Fortsätt
                    </Button>
                  </>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1.25 }}>
                    {subSubcategories.map((sc) => (
                      <Button
                        key={sc._id}
                        variant={subSubCategoryId === sc._id ? 'contained' : 'outlined'}
                        onClick={() => {
                          setSubSubCategoryId(sc._id);
                          setUploadItems([]);
                          setError(null);
                          setSuccess(null);
                          setStep('choose-files');
                        }}
                        sx={{ py: 2, fontWeight: 800, borderRadius: 2, minHeight: 96 }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <StorefrontIcon />
                          <span style={{ textAlign: 'center', lineHeight: 1.1 }}>{sc.name}</span>
                        </Box>
                      </Button>
                    ))}
                  </Box>
                )}
              </>
            )}

            {step === 'choose-files' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                      setStep(subSubcategories.length > 0 ? 'choose-sub2' : 'choose-sub');
                      setUploadItems([]);
                      setError(null);
                      setSuccess(null);
                      try {
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      } catch {}
                    }}
                  >
                    Tillbaka
                  </Button>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Välj bilder
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Kategori: <strong>{selectedMain?.name}</strong>
                  {subCategoryId ? (
                    <>
                      {' '}→{' '}
                      <strong>{categories.find((c) => c._id === subCategoryId)?.name}</strong>
                    </>
                  ) : null}
                  {subSubCategoryId ? (
                    <>
                      {' '}→{' '}
                      <strong>{categories.find((c) => c._id === subSubCategoryId)?.name}</strong>
                    </>
                  ) : null}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    const nextItems: UploadItem[] = files.map((f) => ({
                      id: uuidv4(),
                      file: f,
                      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : '',
                      butikId: bulkButikIdRef.current,
                      description: bulkDescriptionRef.current,
                    }));
                    setUploadItems(nextItems);
                    setStep('metadata');
                  }}
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<PhotoCameraIcon />}
                    onClick={handlePickFiles}
                    disabled={uploading}
                    sx={{ py: 1.75, fontWeight: 800, borderRadius: 2 }}
                  >
                    Välj bilder
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={resetAll}
                    disabled={uploading}
                    sx={{ py: 1.25, borderRadius: 2 }}
                  >
                    Avbryt
                  </Button>
                </Box>
              </>
            )}

            {step === 'metadata' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                      setStep('choose-files');
                      setUploadItems([]);
                      setError(null);
                      setSuccess(null);
                      setBulkButikId('');
                      setBulkDescription('');
                      bulkButikIdRef.current = '';
                      bulkDescriptionRef.current = '';
                      try {
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      } catch {}
                    }}
                  >
                    Tillbaka
                  </Button>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Metadata (per bild)
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedMain?.name ? <>Kategori: <strong>{selectedMain.name}</strong></> : null}
                  {selectedSub?.name ? <> {' '}→{' '}<strong>{selectedSub.name}</strong></> : null}
                  {subSubCategoryId ? <> {' '}→{' '}<strong>{categories.find((c) => c._id === subSubCategoryId)?.name}</strong></> : null}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                {/* Bulk (apply to all) fields */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Samma för alla (valfritt)
                  </Typography>
                  {isButiksbilderFlow && (
                    <TextField
                      label="Butik (alla bilder)"
                      value={bulkButikId}
                      onChange={(e) => applyBulkButik(e.target.value)}
                      placeholder="t.ex. ICA Maxi Kungens Kurva"
                      size="small"
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                  )}
                  <TextField
                    label="Beskrivning (alla bilder)"
                    value={bulkDescription}
                    onChange={(e) => applyBulkDescription(e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Paper>

                <Box sx={{ display: 'grid', gap: 1.25 }}>
                  {uploadItems.map((it) => (
                    <Paper key={it.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        {it.previewUrl ? (
                          <Box
                            component="img"
                            src={it.previewUrl}
                            alt={it.file.name}
                            sx={{ width: 88, height: 88, borderRadius: 2, objectFit: 'cover', bgcolor: 'grey.100', flexShrink: 0 }}
                          />
                        ) : (
                          <Box sx={{ width: 88, height: 88, borderRadius: 2, bgcolor: 'grey.200', flexShrink: 0 }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                            {it.file.name}
                          </Typography>

                          {isButiksbilderFlow && (
                            <TextField
                              label="Butik"
                              value={it.butikId}
                              onChange={(e) => {
                                const v = e.target.value;
                                setUploadItems(prev => prev.map(p => (p.id === it.id ? { ...p, butikId: v } : p)));
                              }}
                              placeholder="t.ex. ICA Maxi Kungens Kurva"
                              size="small"
                              fullWidth
                              sx={{ mt: 1 }}
                            />
                          )}

                          <TextField
                            label="Beskrivning"
                            value={it.description}
                            onChange={(e) => {
                              const v = e.target.value;
                              setUploadItems(prev => prev.map(p => (p.id === it.id ? { ...p, description: v } : p)));
                            }}
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                    onClick={handleUpload}
                    disabled={uploading || uploadItems.length === 0}
                    sx={{ py: 1.75, fontWeight: 800, borderRadius: 2 }}
                  >
                    {uploading ? 'Laddar upp…' : `Ladda upp (${uploadItems.length})`}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={resetAll}
                    disabled={uploading}
                    sx={{ py: 1.25, borderRadius: 2 }}
                  >
                    Avbryt
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}

